import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { COMPANY_INFO } from '../data/company';
import { fetchCmsPageBySlug, fetchCmsServices, fetchCmsGalleryBySlug } from '../lib/cms';
import ReviewsSlider from '../components/ReviewsSlider';
import LightboxModal from '../components/LightboxModal';

export default function HomePage() {
  const [pageData, setPageData] = useState(null);
  const [services, setServices] = useState([]);
  const [gallery, setGallery] = useState(null);
  const [activeImageIndex, setActiveImageIndex] = useState(null);
  const [openFaq, setOpenFaq] = useState(null); // Accordion state - start closed

  // Form State
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service: 'Privatrengøring',
    description: '',
    website_url: '' // Honeypot
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    async function loadData() {
      const page = await fetchCmsPageBySlug('index');
      const svcs = await fetchCmsServices();
      const gal = await fetchCmsGalleryBySlug('forside');
      
      setPageData(page);
      setServices(svcs);
      setGallery(gal);
    }
    loadData();
  }, []);

  const galleryImages = gallery?.images || [
    "https://assets.auxodev.dk/assets/2c387ddb-b88b-45a9-8d4d-8c9017ed8767/rengoering-erhverv-gulvrengoering-i-lang-gang-01.jpg",
    "https://assets.auxodev.dk/assets/2c387ddb-b88b-45a9-8d4d-8c9017ed8767/rengoering-privat-stoevsugning-af-gulv-i-bolig.jpg",
    "https://assets.auxodev.dk/assets/2c387ddb-b88b-45a9-8d4d-8c9017ed8767/haveservice-haekkeklipning-langs-hus.jpg",
    "https://assets.auxodev.dk/assets/2c387ddb-b88b-45a9-8d4d-8c9017ed8767/vinduespolering-indvendig-pudsning-af-glasdoer.jpg",
    "https://assets.auxodev.dk/assets/2c387ddb-b88b-45a9-8d4d-8c9017ed8767/tagrenderensning-rensning-af-tagrende-med-teleskopudstyr.jpg",
    "https://assets.auxodev.dk/assets/2c387ddb-b88b-45a9-8d4d-8c9017ed8767/andet-firmabiler-og-servicekoeretoejer-01.jpg",
    "https://assets.auxodev.dk/assets/2c387ddb-b88b-45a9-8d4d-8c9017ed8767/rengoering-erhverv-rengoering-af-kantine-og-spiseomraade-01.jpg",
    "https://assets.auxodev.dk/assets/2c387ddb-b88b-45a9-8d4d-8c9017ed8767/rengoering-privat-rengoering-af-badevaerelse.jpg",
    "https://assets.auxodev.dk/assets/2c387ddb-b88b-45a9-8d4d-8c9017ed8767/haveservice-klipning-af-formklippede-haekke.jpg"
  ];

  const faqItems = [
    {
      q: "Hvilke typer rengøring tilbyder OP Rengøringsservice?",
      a: "Vi tilbyder et bredt udvalg af serviceydelser: Privatrengøring, erhvervsrengøring til kontorer og erhverv, vinduespolering, haveservice, snerydning, måtteservice samt tagrenderensning i Nibe og omegn."
    },
    {
      q: "Bliver rengøringen udført af faste og kompetente medarbejdere?",
      a: "Ja! Vi har mere end 20 kompetente medarbejdere. Det er altafgørende for os, at du som kunde føler fuld tillid og tryghed ved de medarbejdere, der kommer i dit hjem eller din virksomhed."
    },
    {
      q: "Hvilke geografiske områder dækker I?",
      a: "Vores hovedkontor ligger på Skalhuse 13 i Nibe, og vi kører ud til både private husstande, virksomheder og boligforeninger i Nibe, Aalborg og omegn."
    },
    {
      q: "Hvordan indhenter jeg et uforpligtende tilbud?",
      a: "Du kan enten udfylde vores online kontaktformular her på siden, sende os en e-mail på info@opservice.dk eller ringe direkte til os på tlf. (+45) 98 35 15 47."
    },
    {
      q: "Medbringer I selv alt udstyr og rengøringsmidler?",
      a: "Ja, vores medarbejdere medbringer altid alle nødvendige professionelle redskaber, udstyr og skånsomme rengøringsmidler til opgaven."
    }
  ];

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');

    if (formData.website_url) {
      setSubmitSuccess(true);
      return;
    }

    if (!formData.name.trim() || !formData.phone.trim() || !formData.email.trim()) {
      setSubmitError('Udfyld venligst alle påkrævede felter (navn, telefon og e-mail).');
      return;
    }

    setSubmitting(true);

    try {
      const res = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          service: formData.service,
          description: formData.description,
          website_url: formData.website_url
        })
      });

      if (res.ok) {
        setSubmitSuccess(true);
        setFormData({ name: '', phone: '', email: '', service: 'Privatrengøring', description: '', website_url: '' });
      } else {
        const data = await res.json().catch(() => ({}));
        setSubmitError(data.message || 'Der opstod en fejl ved afsendelsen. Prøv venligst igen eller ring til os.');
      }
    } catch (err) {
      setSubmitError('Netværksfejl ved afsendelse. Kontrollér din forbindelse eller kontakt os direkte på tlf. (+45) 98 35 15 47.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{pageData?.meta_title || "Privat- og Erhvervsrengøring, vinduespolering og haveservice i Nibe · OP RENGØRINGSSERVICE"}</title>
        <meta name="description" content={pageData?.meta_description || "Privat- og Erhvervsrengøring, vinduespolering og haveservice i Nibe · OP RENGØRINGSSERVICE"} />
        <meta property="og:title" content="Privat- og Erhvervsrengøring, vinduespolering og haveservice i Nibe · OP RENGØRINGSSERVICE" />
        <meta property="og:description" content="Tilbyder solide serviceydelser inden for Vinduespudsning, rengøring og haveservice samt snerydning til både private og erhvervskunder." />
        <link rel="canonical" href="https://opservice.dk/" />
      </Helmet>

      {/* ========================================================= */}
      {/* SECTION 1: HERO BANNER (Transparent Ambient Hero Layout) */}
      {/* ========================================================= */}
      <section className="relative w-full bg-slate-900 text-white overflow-hidden pt-20 sm:pt-28 pb-32 sm:pb-40 px-4">
        {/* Background Image with Transparent Overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-55 mix-blend-luminosity scale-105 transition-transform duration-1000"
          style={{ backgroundImage: `url('${pageData?.hero_image?.[0] || 'https://assets.auxodev.dk/assets/2c387ddb-b88b-45a9-8d4d-8c9017ed8767/andet-firmabiler-og-servicekoeretoejer-01.jpg'}')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/60 via-slate-900/50 to-slate-950/70" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center justify-center text-center space-y-6">
          
          {/* Eyebrow Tag */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-500/20 text-blue-300 border border-blue-500/30 font-bold text-xs uppercase tracking-widest backdrop-blur-sm">
            <i className="fa-solid fa-sparkles text-blue-400" aria-hidden="true" />
            <span>{COMPANY_INFO.name} – {COMPANY_INFO.slogan}</span>
          </div>

          {/* Hero Main Headline */}
          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-[1.15] max-w-7xl w-full">
            Privat- og Erhvervsrengøring, vinduespolering og haveservice i Nibe
          </h1>

          {/* Subtitle text */}
          <p className="text-slate-200 text-base sm:text-lg leading-relaxed max-w-2xl font-normal">
            Tilbyder solide serviceydelser inden for vinduespudsning, rengøring, haveservice samt snerydning til både private og erhvervskunder.
          </p>

          {/* Action Row: Primary Button + Contact Ring Launcher */}
          <div className="pt-4 flex flex-wrap items-center justify-center gap-4 sm:gap-6">
            <Link
              to="/kontakt"
              className="rounded-full bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs uppercase tracking-wider px-8 py-4 shadow-xl hover:shadow-blue-600/30 transition-all transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              Få et uforpligtende tilbud
            </Link>
            
            <a
              href={`tel:${COMPANY_INFO.phoneRaw}`}
              className="group rounded-full bg-slate-800/80 hover:bg-slate-800 text-white border border-slate-700 font-bold text-xs uppercase tracking-wider px-6 py-3.5 backdrop-blur-md transition-all flex items-center gap-3 whitespace-nowrap-custom shadow-md"
            >
              <span className="w-8 h-8 rounded-full bg-blue-600 group-hover:bg-blue-500 text-white flex items-center justify-center text-xs shadow-xs transition-transform group-hover:scale-110">
                <i className="fa-solid fa-phone" aria-hidden="true" />
              </span>
              <span>Ring i dag: {COMPANY_INFO.phone}</span>
            </a>
          </div>

        </div>
      </section>

      {/* ========================================================= */}
      {/* SECTION 2: FLOATING 3 FEATURE CARDS OVERLAPPING HERO BOTTOM (WHOLE BOX CLICKABLE) */}
      {/* ========================================================= */}
      <section className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-20 sm:-mt-24 mb-20">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
          
          {/* Card 1: Privatrengøring */}
          <Link
            to="/ydelser/rengoering-privat"
            className="bg-white rounded-3xl p-8 shadow-2xl border border-slate-100/80 hover:-translate-y-1.5 transition-all duration-300 flex flex-col justify-between group relative overflow-hidden block"
          >
            <div className="absolute top-0 right-0 w-24 h-24 bg-blue-50 rounded-bl-full -z-0 group-hover:bg-blue-100 transition-colors" />
            
            <div className="relative z-10">
              <div className="w-14 h-14 rounded-2xl bg-blue-600 text-white flex items-center justify-center text-2xl shadow-md mb-6 group-hover:scale-110 transition-transform">
                <i className="fa-solid fa-house-chimney-window" aria-hidden="true" />
              </div>

              <h3 className="text-xl font-bold text-slate-900 group-hover:text-blue-700 transition-colors mb-2.5">
                Privatrengøring
              </h3>

              <p className="text-slate-600 text-sm leading-relaxed mb-6">
                Skræddersyet rengøring i private hjem med fokus på grundighed, tillid og fleksibilitet tilpasset din hverdag.
              </p>
            </div>

            <div className="relative z-10 inline-flex items-center justify-between text-xs font-bold uppercase tracking-wider text-blue-700 group-hover:text-blue-800 pt-4 border-t border-slate-100 w-full">
              <span>Læs mere om privat</span>
              <i className="fa-solid fa-arrow-right transition-transform group-hover:translate-x-1" aria-hidden="true" />
            </div>
          </Link>

          {/* Card 2: Erhvervsrengøring */}
          <Link
            to="/ydelser/rengoering-erhverv"
            className="bg-white rounded-3xl p-8 shadow-2xl border border-slate-100/80 hover:-translate-y-1.5 transition-all duration-300 flex flex-col justify-between group relative overflow-hidden block"
          >
            <div className="absolute top-0 right-0 w-24 h-24 bg-blue-50 rounded-bl-full -z-0 group-hover:bg-blue-100 transition-colors" />
            
            <div className="relative z-10">
              <div className="w-14 h-14 rounded-2xl bg-blue-600 text-white flex items-center justify-center text-2xl shadow-md mb-6 group-hover:scale-110 transition-transform">
                <i className="fa-solid fa-building-user" aria-hidden="true" />
              </div>

              <h3 className="text-xl font-bold text-slate-900 group-hover:text-blue-700 transition-colors mb-2.5">
                Erhvervsrengøring
              </h3>

              <p className="text-slate-600 text-sm leading-relaxed mb-6">
                Professionelle rengøringsaftaler til kontorer, erhverv, butikker og institutioner i Nibe og omegn.
              </p>
            </div>

            <div className="relative z-10 inline-flex items-center justify-between text-xs font-bold uppercase tracking-wider text-blue-700 group-hover:text-blue-800 pt-4 border-t border-slate-100 w-full">
              <span>Læs mere om erhverv</span>
              <i className="fa-solid fa-arrow-right transition-transform group-hover:translate-x-1" aria-hidden="true" />
            </div>
          </Link>

          {/* Card 3: Haveservice & Snerydning */}
          <Link
            to="/ydelser/haveservice"
            className="bg-white rounded-3xl p-8 shadow-2xl border border-slate-100/80 hover:-translate-y-1.5 transition-all duration-300 flex flex-col justify-between group relative overflow-hidden block"
          >
            <div className="absolute top-0 right-0 w-24 h-24 bg-blue-50 rounded-bl-full -z-0 group-hover:bg-blue-100 transition-colors" />
            
            <div className="relative z-10">
              <div className="w-14 h-14 rounded-2xl bg-blue-600 text-white flex items-center justify-center text-2xl shadow-md mb-6 group-hover:scale-110 transition-transform">
                <i className="fa-solid fa-tree-city" aria-hidden="true" />
              </div>

              <h3 className="text-xl font-bold text-slate-900 group-hover:text-blue-700 transition-colors mb-2.5">
                Haveservice & Snerydning
              </h3>

              <p className="text-slate-600 text-sm leading-relaxed mb-6">
                Vedligeholdelse af grønne arealer, græsslåning, hækklipning samt vinterklar snerydning og salting.
              </p>
            </div>

            <div className="relative z-10 inline-flex items-center justify-between text-xs font-bold uppercase tracking-wider text-blue-700 group-hover:text-blue-800 pt-4 border-t border-slate-100 w-full">
              <span>Læs mere om haveservice</span>
              <i className="fa-solid fa-arrow-right transition-transform group-hover:translate-x-1" aria-hidden="true" />
            </div>
          </Link>

        </div>
      </section>

      {/* ========================================================= */}
      {/* SECTION 3: ABOUT / OUR COMPANY SECTION */}
      {/* ========================================================= */}
      <section className="relative w-full bg-white py-16 sm:py-24 border-b border-slate-100 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            {/* Left Column: Image Container */}
            <div className="lg:col-span-6 relative">
              <div className="relative z-10 rounded-3xl overflow-hidden shadow-2xl border-4 border-slate-50 bg-slate-100 aspect-[4/3]">
                <img
                  src={pageData?.secondary_images?.[0] || "https://assets.auxodev.dk/assets/2c387ddb-b88b-45a9-8d4d-8c9017ed8767/andet-gruppebillede-af-medarbejdere-01.jpg"}
                  alt="OP Rengøringsservice medarbejdere"
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Floating Info Pill Card */}
              <div className="absolute -bottom-6 -right-2 sm:right-6 z-20 bg-white p-4 sm:p-5 rounded-2xl shadow-2xl border border-slate-100 flex items-center gap-4 max-w-xs">
                <div className="w-12 h-12 rounded-xl bg-blue-100 text-blue-700 flex items-center justify-center text-2xl shrink-0">
                  <i className="fa-solid fa-users" aria-hidden="true" />
                </div>
                <div>
                  <span className="block font-extrabold text-slate-900 text-sm sm:text-base leading-tight">20+ Medarbejdere</span>
                  <span className="text-xs text-slate-500 font-medium">{COMPANY_INFO.slogan}</span>
                </div>
              </div>

            </div>

            {/* Right Column: Content Block */}
            <div className="lg:col-span-6 space-y-6">
              
              <div>
                <span className="text-blue-600 font-bold uppercase tracking-widest text-xs block mb-2">
                  OM OP RENGØRINGSSERVICE
                </span>
                <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight leading-tight">
                  Vi VIL være de bedste til de opgaver, vi udfører
                </h2>
              </div>

              <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
                Tilbyder solide serviceydelser inden for vinduespudsning, rengøring, haveservice samt snerydning til både private og erhvervskunder. Vi er mere end 20 kompetente medarbejdere, og det er altafgørende for os, at du som kunde føler tillid til os og det stykke servicearbejde, som vi udfører.
              </p>

              {/* 2x2 Sub-feature Grid with Blue Checkmarks */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                <div className="flex items-start gap-3 bg-slate-50 p-3.5 rounded-2xl border border-slate-100">
                  <div className="w-7 h-7 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center shrink-0 mt-0.5">
                    <i className="fa-solid fa-check text-xs" aria-hidden="true" />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wide">Privat & Erhverv</h4>
                    <p className="text-slate-500 text-xs mt-0.5">Komplette løsninger</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 bg-slate-50 p-3.5 rounded-2xl border border-slate-100">
                  <div className="w-7 h-7 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center shrink-0 mt-0.5">
                    <i className="fa-solid fa-check text-xs" aria-hidden="true" />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wide">Vinduespolering</h4>
                    <p className="text-slate-500 text-xs mt-0.5">Striberfrie ruder</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 bg-slate-50 p-3.5 rounded-2xl border border-slate-100">
                  <div className="w-7 h-7 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center shrink-0 mt-0.5">
                    <i className="fa-solid fa-check text-xs" aria-hidden="true" />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wide">Haveservice</h4>
                    <p className="text-slate-500 text-xs mt-0.5">Græs & snerydning</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 bg-slate-50 p-3.5 rounded-2xl border border-slate-100">
                  <div className="w-7 h-7 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center shrink-0 mt-0.5">
                    <i className="fa-solid fa-check text-xs" aria-hidden="true" />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wide">Lokalt i Nibe</h4>
                    <p className="text-slate-500 text-xs mt-0.5">Skalhuse 13</p>
                  </div>
                </div>
              </div>

              {/* Action Link */}
              <div className="pt-3">
                <Link
                  to="/hvem-er-vi"
                  className="inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs uppercase tracking-wider px-8 py-4 shadow-md transition-all"
                >
                  <span>Læs mere om os</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
              </div>

            </div>

          </div>
        </div>
      </section>

      {/* ========================================================= */}
      {/* SECTION 4: SERVICES SECTION (Dark Navy Slate Background) */}
      {/* ========================================================= */}
      <section className="w-full bg-slate-900 text-white py-20 lg:py-28 relative overflow-hidden">
        
        {/* Subtle Ambient Glow */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-sky-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          
          {/* Header Row */}
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6 border-b border-slate-800 pb-8">
            <div>
              <span className="text-blue-400 font-bold uppercase tracking-widest text-xs block mb-2">
                VORES YDELSER
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
                Vores Rengørings- og Serviceydelser
              </h2>
            </div>

            <Link
              to="/ydelser"
              className="inline-flex items-center gap-2 rounded-full border border-slate-700 hover:border-blue-500 bg-slate-800/80 hover:bg-slate-800 text-slate-200 hover:text-white font-bold text-xs uppercase tracking-wider px-6 py-3 transition-all shrink-0"
            >
              <span>Se alle ydelser</span>
              <i className="fa-solid fa-arrow-right text-xs text-blue-400" aria-hidden="true" />
            </Link>
          </div>

          {/* 3 Dark Service Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {services.slice(0, 3).map((svc) => (
              <div
                key={svc.slug.da || svc.slug}
                className="bg-slate-800/90 border border-slate-700/80 rounded-3xl overflow-hidden hover:border-blue-500/50 transition-all duration-300 group flex flex-col justify-between shadow-xl relative"
              >
                <div>
                  {/* Image with Floating Action Button Overlapping Border */}
                  <div className="relative aspect-[16/10] bg-slate-950 overflow-visible">
                    <div className="absolute inset-0 overflow-hidden rounded-t-3xl">
                      <img
                        src={svc.primary_image?.[0] || "https://assets.auxodev.dk/assets/2c387ddb-b88b-45a9-8d4d-8c9017ed8767/rengoering-erhverv-gulvrengoering-i-lang-gang-01.jpg"}
                        alt={svc.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 opacity-90 group-hover:opacity-100"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent opacity-60" />
                    </div>

                    {/* Floating Round Action Button positioned right at the image-content intersection */}
                    <Link
                      to={`/ydelser/${svc.slug.da || svc.slug}`}
                      className="absolute bottom-0 right-6 translate-y-1/2 z-30 w-12 h-12 rounded-full bg-blue-600 group-hover:bg-blue-500 text-white flex items-center justify-center text-lg shadow-2xl transition-transform duration-300 group-hover:scale-110"
                      aria-label={`Læs mere om ${svc.title}`}
                    >
                      <i className="fa-solid fa-arrow-up-right-from-square text-sm" aria-hidden="true" />
                    </Link>
                  </div>

                  <div className="p-8 pt-8">
                    <h3 className="text-xl font-bold text-white group-hover:text-blue-400 transition-colors mb-3">
                      {svc.title}
                    </h3>
                    <p className="text-slate-300 text-sm leading-relaxed line-clamp-3">
                      {svc.short_description}
                    </p>
                  </div>
                </div>

                <div className="p-8 pt-0">
                  <Link
                    to={`/ydelser/${svc.slug.da || svc.slug}`}
                    className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-blue-400 group-hover:text-blue-300 transition-colors pt-4 border-t border-slate-700/60 w-full justify-between"
                  >
                    <span>Læs mere</span>
                    <i className="fa-solid fa-arrow-right transition-transform group-hover:translate-x-1" aria-hidden="true" />
                  </Link>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* ========================================================= */}
      {/* SECTION 5: "WHO WE ARE" FAQ + LEAD CAPTURE FORM DUAL SECTION */}
      {/* ========================================================= */}
      <section className="w-full bg-slate-50 py-20 lg:py-28 border-y border-slate-200/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            
            {/* Left Column: FAQ Accordion */}
            <div className="lg:col-span-6 space-y-6">
              <div>
                <span className="text-blue-600 font-bold uppercase tracking-widest text-xs block mb-2">
                  HVEM ER VI & SPØRGSMÅL
                </span>
                <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight leading-tight">
                  Ofte stillede spørgsmål om vores serviceydelser
                </h2>
                <p className="text-slate-600 text-sm mt-3 leading-relaxed">
                  Har du spørgsmål til vores rengøringsaftaler, medarbejdere eller dækningsområder? Her har vi samlet de mest almindelige spørgsmål.
                </p>
              </div>

              {/* Accordion Container with Smooth Transitions */}
              <div className="space-y-4 pt-2">
                {faqItems.map((faq, idx) => {
                  const isOpen = openFaq === idx;
                  return (
                    <div
                      key={idx}
                      className="bg-white rounded-2xl border border-slate-200 shadow-xs overflow-hidden transition-all duration-300"
                    >
                      <button
                        type="button"
                        onClick={() => setOpenFaq(isOpen ? null : idx)}
                        className="w-full px-6 py-4 text-left font-bold text-slate-900 text-sm sm:text-base flex items-center justify-between gap-4 hover:text-blue-700 transition-colors focus:outline-none"
                      >
                        <span>{faq.q}</span>
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-all duration-300 ${isOpen ? 'bg-blue-600 text-white rotate-180' : 'bg-slate-100 text-slate-600'}`}>
                          <i className={`fa-solid ${isOpen ? 'fa-minus' : 'fa-plus'} text-xs`} aria-hidden="true" />
                        </div>
                      </button>

                      <div 
                        className={`grid transition-[grid-template-rows,opacity] duration-300 ease-in-out ${
                          isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                        }`}
                      >
                        <div className="overflow-hidden">
                          <div className="px-6 pb-5 pt-1 text-slate-600 text-sm leading-relaxed border-t border-slate-100">
                            {faq.a}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Right Column: Lead Capture Form Card */}
            <div className="lg:col-span-6">
              <div className="bg-white rounded-3xl p-8 sm:p-10 shadow-2xl border border-slate-100 relative">
                
                <div className="mb-6">
                  <span className="text-xs font-bold text-blue-700 uppercase tracking-widest bg-blue-50 px-3 py-1 rounded-full border border-blue-100 inline-block mb-3">
                    UFORPLIGTENDE TILBUD
                  </span>
                  <h3 className="text-2xl font-extrabold text-slate-900">
                    Få et uforpligtende tilbud
                  </h3>
                  <p className="text-slate-500 text-xs mt-1">
                    Udfyld formularen, så kontakter vi dig inden for 24 timer.
                  </p>
                </div>

                {submitSuccess ? (
                  <div className="py-12 text-center animate-in fade-in duration-300 space-y-4">
                    <div className="w-16 h-16 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-3xl mx-auto">
                      <i className="fa-solid fa-circle-check" aria-hidden="true" />
                    </div>
                    <h4 className="text-xl font-bold text-slate-900">Mange tak for din henvendelse!</h4>
                    <p className="text-slate-600 text-sm max-w-sm mx-auto">
                      Vi har modtaget dine oplysninger og vender tilbage med et uforpligtende tilbud hurtigst muligt.
                    </p>
                    <button
                      type="button"
                      onClick={() => setSubmitSuccess(false)}
                      className="mt-4 rounded-full bg-slate-900 text-white font-bold text-xs uppercase tracking-wider px-6 py-3 transition-colors"
                    >
                      Send ny besked
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleFormSubmit} className="space-y-4" noValidate>
                    
                    {/* ANTISPAM HONEYPOT (STRICT REQUIREMENT) */}
                    <div className="sr-only absolute -left-[9999px]" aria-hidden="true">
                      <label htmlFor="website_url">Website URL (do not fill)</label>
                      <input
                        type="text"
                        id="website_url"
                        name="website_url"
                        value={formData.website_url}
                        onChange={handleFormChange}
                        tabIndex={-1}
                        autoComplete="off"
                      />
                    </div>

                    {submitError && (
                      <div className="p-3.5 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-xs flex items-start gap-2.5">
                        <i className="fa-solid fa-triangle-exclamation text-rose-500 mt-0.5" aria-hidden="true" />
                        <span>{submitError}</span>
                      </div>
                    )}

                    <div>
                      <label htmlFor="home_name" className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                        Navn <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="home_name"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleFormChange}
                        placeholder="Dit fulde navn"
                        className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm transition-all"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="home_phone" className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                          Telefon <span className="text-rose-500">*</span>
                        </label>
                        <input
                          type="tel"
                          id="home_phone"
                          name="phone"
                          required
                          value={formData.phone}
                          onChange={handleFormChange}
                          placeholder="f.eks. 98351547"
                          className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm transition-all"
                        />
                      </div>

                      <div>
                        <label htmlFor="home_service" className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                          Ønsket Ydelse
                        </label>
                        <select
                          id="home_service"
                          name="service"
                          value={formData.service}
                          onChange={handleFormChange}
                          className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm transition-all bg-white"
                        >
                          <option value="Privatrengøring">Privatrengøring</option>
                          <option value="Erhvervsrengøring">Erhvervsrengøring</option>
                          <option value="Vinduespolering">Vinduespolering</option>
                          <option value="Haveservice">Haveservice</option>
                          <option value="Snerydning">Snerydning</option>
                          <option value="Andet">Andet</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="home_email" className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                        E-mailadresse <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="home_email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleFormChange}
                        placeholder="din.email@eksempel.dk"
                        className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm transition-all"
                      />
                    </div>

                    <div>
                      <label htmlFor="home_description" className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                        Beskrivelse af opgaven
                      </label>
                      <textarea
                        id="home_description"
                        name="description"
                        rows={3}
                        value={formData.description}
                        onChange={handleFormChange}
                        placeholder="Skriv kort om dine ønsker..."
                        className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm transition-all resize-y"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold uppercase tracking-wider text-xs py-4 rounded-xl shadow-lg transition-all flex items-center justify-center gap-2"
                    >
                      {submitting ? (
                        <>
                          <i className="fa-solid fa-spinner animate-spin text-sm" aria-hidden="true" />
                          <span>Sender...</span>
                        </>
                      ) : (
                        <>
                          <span>Indhent tilbud nu</span>
                          <i className="fa-solid fa-paper-plane text-xs" aria-hidden="true" />
                        </>
                      )}
                    </button>

                  </form>
                )}

              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ========================================================= */}
      {/* SECTION 6: "WHY CHOOSE US" RESTYLED TO DESIGN SPEC REFERENCE */}
      {/* (Minimal layout with thin left borders, eyebrow accent, clean typography) */}
      {/* ========================================================= */}
      <section className="w-full bg-white py-20 lg:py-28 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          
          {/* Top Header Block */}
          <div className="text-left space-y-3">
            <span className="text-blue-600 font-bold uppercase tracking-widest text-xs block">
              HVORFOR VÆLGE OS
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light text-slate-900 tracking-tight leading-tight">
              6 Grunde til at vælge OP Rengøringsservice
            </h2>
            <div className="w-16 h-1 bg-blue-600 rounded-full my-4" />
          </div>

          {/* Bottom Feature Grid (Minimal layout with left borders, responsive 3-col on desktop, 2-col on tablet, 1-col on mobile) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
            
            {/* Feature 1 */}
            <div className="border-l-2 border-blue-600 pl-6 space-y-2">
              <span className="text-blue-600 font-bold uppercase tracking-widest text-xs block">
                01 / TILLID
              </span>
              <h3 className="text-lg font-bold text-slate-900 uppercase tracking-wide">
                Kvalitet & Tillid
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Det er altafgørende for os, at du som kunde føler tillid til os og det stykke servicearbejde, vi udfører.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="border-l-2 border-blue-600 pl-6 space-y-2">
              <span className="text-blue-600 font-bold uppercase tracking-widest text-xs block">
                02 / ERFARING
              </span>
              <h3 className="text-lg font-bold text-slate-900 uppercase tracking-wide">
                20+ Medarbejdere
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Vi har mere end 20 kompetente og venlige medarbejdere klar til at levere stabile og grundige resultater. Vores medarbejdere er lønnet efter serviceoverenskomsten.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="border-l-2 border-blue-600 pl-6 space-y-2">
              <span className="text-blue-600 font-bold uppercase tracking-widest text-xs block">
                03 / LOKALT
              </span>
              <h3 className="text-lg font-bold text-slate-900 uppercase tracking-wide">
                Lokalt i Nibe
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Med base på Skalhuse 13 servicerer vi med stolthed hele Nibe og tilstødende områder.
              </p>
            </div>

            {/* Feature 4 */}
            <div className="border-l-2 border-blue-600 pl-6 space-y-2">
              <span className="text-blue-600 font-bold uppercase tracking-widest text-xs block">
                04 / UDSTYR
              </span>
              <h3 className="text-lg font-bold text-slate-900 uppercase tracking-wide">
                Alt Udstyr Medbringes
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Vi medbringer altid professionelle maskiner, redskaber og miljøvenlige rengøringsmidler.
              </p>
            </div>

            {/* Feature 5 */}
            <div className="border-l-2 border-blue-600 pl-6 space-y-2">
              <span className="text-blue-600 font-bold uppercase tracking-widest text-xs block">
                05 / AFTALER
              </span>
              <h3 className="text-lg font-bold text-slate-900 uppercase tracking-wide">
                Skræddersyede Aftaler
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Aftaler tilpasses præcis dine ønsker uden ubehagelige bindinger eller skjulte gebyrer.
              </p>
            </div>

            {/* Feature 6 */}
            <div className="border-l-2 border-blue-600 pl-6 space-y-2">
              <span className="text-blue-600 font-bold uppercase tracking-widest text-xs block">
                06 / MILJØ
              </span>
              <h3 className="text-lg font-bold text-slate-900 uppercase tracking-wide">
                Miljø & Kvalitetsfokus
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Vi anvender skånsomme metoder og overholder altid gældende godkendelsesstandarder.
              </p>
            </div>

          </div>

        </div>
      </section>

      {/* ========================================================= */}
      {/* SECTION 7: WORK SHOWCASE / GALLERY GRID (TEXTS REMOVED FROM IMAGES) */}
      {/* ========================================================= */}
      <section className="w-full bg-slate-50 py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="text-center max-w-2xl mx-auto mb-14 space-y-2">
            <span className="text-blue-600 font-bold uppercase tracking-widest text-xs block">
              BILLEDGALLERI
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
              Billeder fra vores hverdagsarbejde
            </h2>
          </div>

          {/* 6 Grid Cards with Clean Images (All text overlays removed) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {galleryImages.slice(0, 6).map((imgUrl, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => setActiveImageIndex(idx)}
                className="group relative rounded-3xl overflow-hidden bg-slate-100 aspect-[4/3] shadow-sm hover:shadow-xl transition-all block text-left border border-slate-200/80 focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label={`Forstør billede ${idx + 1}`}
              >
                <img
                  src={imgUrl}
                  alt={`Billede ${idx + 1}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              </button>
            ))}
          </div>

          {/* Full-width Callout Banner under Gallery */}
          <div className="mt-12 bg-blue-600 text-white rounded-3xl p-8 sm:p-10 shadow-xl flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="space-y-1 text-center md:text-left">
              <h3 className="text-2xl font-bold">Skal vi tage hånd om din næste rengøringsopgave?</h3>
              <p className="text-blue-100 text-sm">
                Kontakt os i dag for et uforpligtende tilbud tilpasset dine ønsker.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3 shrink-0">
              <a
                href={`tel:${COMPANY_INFO.phoneRaw}`}
                className="bg-white text-blue-900 hover:bg-slate-100 font-bold text-xs uppercase tracking-wider px-6 py-3.5 rounded-full shadow-md transition-colors whitespace-nowrap-custom"
              >
                <i className="fa-solid fa-phone mr-2 text-blue-600" aria-hidden="true" />
                <span>Ring: {COMPANY_INFO.phone}</span>
              </a>
            </div>
          </div>

        </div>
      </section>

      {/* REVIEWS SLIDER */}
      <ReviewsSlider />

      {/* LIGHTBOX MODAL */}
      <LightboxModal
        images={galleryImages}
        activeIndex={activeImageIndex}
        onClose={() => setActiveImageIndex(null)}
        onPrev={() => setActiveImageIndex((prev) => (prev > 0 ? prev - 1 : galleryImages.length - 1))}
        onNext={() => setActiveImageIndex((prev) => (prev < galleryImages.length - 1 ? prev + 1 : 0))}
      />
    </>
  );
}
